import { useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [key, setKey] = useState(sessionStorage.getItem("ag_admin_key") || "");
  const [authed, setAuthed] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (k) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/enquiries`, { headers: { "X-Admin-Key": k } });
      setEnquiries(res.data);
      setAuthed(true);
      sessionStorage.setItem("ag_admin_key", k);
    } catch {
      setError("Invalid admin key or unable to load enquiries.");
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    load(key);
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-ivory pt-32 pb-24">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12">
        <div className="flex items-center gap-4 mb-3">
          <span className="h-px w-10 bg-gold/60" />
          <span className="text-[11px] tracking-[0.32em] uppercase text-navy/60">Internal</span>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl font-medium text-navy">Enquiries Dashboard</h1>

        {!authed ? (
          <form data-testid="admin-login-form" onSubmit={login} className="mt-12 max-w-md">
            <label className="block">
              <span className="text-[11px] tracking-[0.22em] uppercase text-navy/50">Admin Key</span>
              <input
                data-testid="admin-key-input"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Enter admin key"
                className="w-full bg-transparent border-b border-navy/20 focus:border-gold outline-none py-3 text-navy transition-colors duration-300"
              />
            </label>
            {error && <p data-testid="admin-error" className="text-red-700 text-sm mt-4">{error}</p>}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 mt-8 bg-navy text-ivory text-[13px] tracking-[0.12em] uppercase px-8 py-4 transition-colors duration-300 hover:bg-gold hover:text-navy disabled:opacity-60"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Lock size={15} />}
              View Enquiries
            </button>
          </form>
        ) : (
          <div className="mt-12">
            <div className="flex items-center justify-between mb-6">
              <p data-testid="enquiry-count" className="text-navy/60 text-sm">
                {enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"} received
              </p>
              <button
                data-testid="admin-refresh-button"
                onClick={() => load(key)}
                className="inline-flex items-center gap-2 text-[12px] tracking-[0.14em] uppercase text-navy/70 hover:text-gold transition-colors duration-300"
              >
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {enquiries.length === 0 ? (
              <p data-testid="no-enquiries" className="text-navy/50 font-light py-16 text-center border border-navy/10">
                No enquiries yet.
              </p>
            ) : (
              <div className="space-y-4">
                {enquiries.map((enq) => (
                  <div key={enq.id} data-testid={`enquiry-card-${enq.id}`} className="bg-white border border-navy/10 p-6 md:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-serif text-xl text-navy">{enq.name}</p>
                        <p className="text-sm text-navy/50">{enq.company || "—"}</p>
                      </div>
                      <div className="text-right">
                        <span className="inline-block text-[11px] tracking-[0.14em] uppercase bg-linen/60 text-navy px-3 py-1.5">
                          {enq.product}
                        </span>
                        <p className="text-xs text-navy/40 mt-2">
                          {new Date(enq.created_at).toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-4 mt-5 text-sm text-navy/70">
                      <p><span className="text-navy/40">Phone:</span> {enq.phone}</p>
                      <p className="break-all"><span className="text-navy/40">Email:</span> {enq.email}</p>
                      <p><span className="text-navy/40">Qty:</span> {enq.quantity || "—"}</p>
                    </div>
                    {enq.message && (
                      <p className="text-sm text-navy/70 font-light mt-4 pt-4 border-t border-navy/10">{enq.message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;
